import React, {
  MouseEvent,
  FunctionComponent,
  useState,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  BundlesMonitorOrdersTableWrapperProps,
  MonitorLiveViewModes,
  Types,
} from '../Monitor.types';
import {
  pickupOnTop,
  reorderBundleWithAlgorithm,
  reorderDraftNew,
  reverseDeliveries,
} from '../../../oldPlanner/utils/bundleDraft';
import { recreateBundle } from '../../../../utils/bundlesMonitorUtils';
import { OrderInstruction } from '../../../../components/maps/BundleMap';
import Swal from 'sweetalert2';
import { ButtonComponent } from '../../../../ReusableComponents/Button/ButtonComponent';
import _ from 'lodash';
import './MonitorOrdersTable.scss';
import { MonitorOrdersTable } from './MonitorOrdersTable';
import { cancelBundle } from '../../../bundles/components/BundleUtils';
import { BundleJson, ReorderMode } from '../../../../types/bundleTypes';

export function createFooter(
  modeActive: MonitorLiveViewModes,
  movePickupOnTop: () => Promise<void>,
  onClickCancelBundleButton: () => void,
  triggerRecreateBundle: () => void,
  currentSelectedRows: string[],
  backToBundleNormalView: () => void,
  toggleMoveOrdersModalCallback: () => void,
  reverseDeliveriesCallback: () => void,
  toggleShelfModalCallback: () => void,
  toggleETAModalCallback: () => void,
  reorderButtonActionCallback: (e: MouseEvent<HTMLButtonElement>) => void,
  changePickupPointCallback: () => void
): JSX.Element {
  switch (true) {
    case modeActive === 'split':
      return (
        <div className="orders-table-footer">
          <ButtonComponent variant="text" onClick={backToBundleNormalView}>
            CANCEL SPLIT
          </ButtonComponent>
          <ButtonComponent
            variant="text"
            onClick={triggerRecreateBundle}
            disabled={!currentSelectedRows.length}
          >
            SPLIT SELECTED
          </ButtonComponent>
        </div>
      );
    case modeActive === 'move':
      return (
        <div className="orders-table-footer">
          <ButtonComponent variant="text" onClick={backToBundleNormalView}>
            CANCEL MOVE
          </ButtonComponent>
          <ButtonComponent
            variant="text"
            onClick={toggleMoveOrdersModalCallback}
            disabled={!currentSelectedRows.length}
          >
            MOVE SELECTED
          </ButtonComponent>
        </div>
      );
    default:
      return (
        <div className="orders-table-footer">
          <ButtonComponent variant="text" onClick={onClickCancelBundleButton}>
            CANCEL BUNDLE
          </ButtonComponent>
          <ButtonComponent variant="text" onClick={movePickupOnTop}>
            PICKUP TOP
          </ButtonComponent>
          <ButtonComponent variant="text" onClick={toggleShelfModalCallback}>
            SET SHELF
          </ButtonComponent>
          <ButtonComponent variant="text" onClick={toggleETAModalCallback}>
            ETA CALC.
          </ButtonComponent>
          <ButtonComponent variant="text" onClick={reorderButtonActionCallback}>
            REORDER
          </ButtonComponent>
          <ButtonComponent variant="text" onClick={reverseDeliveriesCallback}>
            REVERSE DELIVERIES
          </ButtonComponent>
          <ButtonComponent variant="text" onClick={changePickupPointCallback}>
            CHANGE PICKUP
          </ButtonComponent>
        </div>
      );
  }
}

const checkProps = (
  prevProps: Readonly<BundlesMonitorOrdersTableWrapperProps>,
  nextProps: Readonly<BundlesMonitorOrdersTableWrapperProps>
) =>
  !!(
    _.isEqual(
      prevProps.currentOrdersInstructions,
      nextProps.currentOrdersInstructions
    ) &&
    prevProps.bundleId === nextProps.bundleId &&
    prevProps.isBundleOpen === nextProps.isBundleOpen &&
    prevProps.isBundleLoading === nextProps.isBundleLoading &&
    prevProps.modeActive === nextProps.modeActive &&
    _.isEqual(prevProps.lastSelectedOrders, nextProps.lastSelectedOrders) &&
    _.isEqual(prevProps.lastSelectedOrders, nextProps.lastSelectedOrders)
  );

const MonitorOrdersTableWrapper: FunctionComponent<BundlesMonitorOrdersTableWrapperProps> =
  memo(
    ({
      bundle,
      bundleId,
      modeActive,
      setBundleViewMode,
      toggleETAModal,
      toggleShelfModal,
      updateSelectedBundleOrBundlesCallback,
      region,
      dispatch,
      currentOrdersInstructions,
      lastSelectedOrders,
      isBundleOpen,
      isBundleLoading,
      toggleMoveOrdersModal,
      togglePickupPointModal,
    }) => {
      const [currentSelectedRows, setSelectedRows] =
        useState<string[]>(lastSelectedOrders);

      const onSelectOrderInstruction = (
        orderInstructions: Array<OrderInstruction>
      ): void => {
        const orders = orderInstructions.map(
          orderInstruction => orderInstruction.order.id
        );
        setSelectedRows(orders);
      };

      const reorder = useCallback(
        async (orders: Array<OrderInstruction>) => {
          try {
            dispatch({
              type: Types.SetLoadingForBundle,
              payload: { bundleId, bundlesLoadingStatus: true },
            });
            await reorderDraftNew(
              bundleId,
              orders.map(row => row.instruction)
            );
          } catch (error) {
            dispatch({
              type: Types.SetLoadingForBundle,
              payload: { bundleId, bundlesLoadingStatus: false },
            });
            await Swal.fire(
              'Error reordering',
              (error as Error).message,
              'error'
            );
          }
        },
        [bundleId, dispatch]
      );

      const movePickupOnTop = useCallback(async () => {
        dispatch({
          type: Types.SetLoadingForBundle,
          payload: { bundleId, bundlesLoadingStatus: true },
        });
        reorderDraftNew(
          bundleId,
          pickupOnTop(currentOrdersInstructions).map(
            orderInstruction => orderInstruction.instruction
          )
        )
          .then(() => console.log('reordered'))
          .catch(error => {
            console.log(error);
            dispatch({
              type: Types.SetLoadingForBundle,
              payload: { bundleId, bundlesLoadingStatus: false },
            });
          });
      }, [bundleId, currentOrdersInstructions, dispatch]);

      const onClickCancelBundleButton = useCallback(() => {
        Swal.fire({
          title: 'Cancel bundle',
          text: `Are you sure you want to cancel bundle ${bundleId}? This is a irreversible action. All the orders in the bundle will change their status to Ready.`,
          icon: 'warning',
          showCancelButton: true,
          focusCancel: true,
          confirmButtonText: 'Cancel bundle',
          confirmButtonColor: '#d33',
          cancelButtonText: 'Never mind',
          cancelButtonColor: '#ccc',
        })
          .then(async result => {
            if (result.value) {
              const cancelResult = await cancelBundle(bundleId);
              if (cancelResult) {
                await Swal.fire(
                  'Success!',
                  `Bundle ${bundleId} is cancelled`,
                  'success'
                );
              } else {
                await Swal.fire(
                  'Bundle could not be cancelled',
                  undefined,
                  'error'
                );
              }
              dispatch({
                type: Types.DeleteBundle,
                payload: { bundleId },
              });
            }
          })
          .catch(error => console.error(error.message));
      }, [bundleId, dispatch]);

      const backToBundleNormalView = useCallback(
        () => setBundleViewMode('normal'),
        [setBundleViewMode]
      );

      const toggleShelfModalCallback = useCallback(() => {
        toggleShelfModal(prevState => ({
          isOpen: !prevState.isOpen,
          bundleId,
        }));
      }, [bundleId, toggleShelfModal]);

      const toggleETAModalCallback = useCallback(() => {
        toggleETAModal(prevState => ({
          isOpen: !prevState.isOpen,
          bundleId,
        }));
      }, [bundleId, toggleETAModal]);

      const reverseDeliveriesCallback = useCallback(async () => {
        await reorder(reverseDeliveries(currentOrdersInstructions));
      }, [currentOrdersInstructions, reorder]);

      const onSaveDndDraft = async (
        _: BundleJson,
        newOrderInstructions: Array<OrderInstruction>
      ) => {
        await reorder(newOrderInstructions);
      };

      const reorderButtonActionCallback = useCallback(
        async e => {
          Swal.fire({
            title: 'Reorder bundle',
            text: `Are you sure you want to reorder bundle ${bundleId}?`,
            icon: 'warning',
            showCancelButton: true,
            focusCancel: true,
            confirmButtonText: 'Reorder bundle',
            confirmButtonColor: '#d33',
            cancelButtonText: 'Cancel',
            cancelButtonColor: '#ccc',
          })
            .then(async result => {
              if (result.value) {
                dispatch({
                  type: Types.SetLoadingForBundle,
                  payload: { bundleId, bundlesLoadingStatus: true },
                });
                await reorderBundleWithAlgorithm(
                  e.shiftKey ? ReorderMode.AutoBundler : ReorderMode.OpenRoute,
                  bundleId,
                  () => {
                    console.log('update done, waiting for pusher call');
                  },
                  error => {
                    Swal.fire({
                      title: `Something unexpected occurred when re-ordering bundle. Error response: ${JSON.stringify(
                        error
                      )}`,
                      icon: 'error',
                    });
                    dispatch({
                      type: Types.SetLoadingForBundle,
                      payload: {
                        bundleId,
                        bundlesLoadingStatus: false,
                      },
                    });
                  }
                );
              }
            })
            .catch(error => console.error(error.message));
        },
        [bundleId, dispatch]
      );

      const toggleMoveOrdersModalCallback = useCallback(
        () =>
          toggleMoveOrdersModal({
            isOpen: true,
            selectedOrdersToMove: currentSelectedRows,
            bundleId,
          }),
        [bundleId, currentSelectedRows, toggleMoveOrdersModal]
      );

      const toggleChangePickupPointModalCallback = useCallback(
        () =>
          togglePickupPointModal({
            isOpen: true,
            bundleId,
          }),
        [bundleId, togglePickupPointModal]
      );

      const triggerRecreateBundle = useCallback(async () => {
        dispatch({
          type: Types.SetLoadingForBundle,
          payload: { bundleId, bundlesLoadingStatus: true },
        });
        await recreateBundle(
          currentSelectedRows,
          region,
          bundleId,
          dispatch,
          setBundleViewMode,
          setSelectedRows,
          updateSelectedBundleOrBundlesCallback
        );
        dispatch({
          type: Types.StoreLastChangesInBundle,
          payload: { bundleId },
        });
      }, [
        bundleId,
        currentSelectedRows,
        dispatch,
        region,
        setBundleViewMode,
        updateSelectedBundleOrBundlesCallback,
      ]);

      useEffect(() => {
        if (currentSelectedRows.length && isBundleOpen && isBundleLoading) {
          setSelectedRows([]);
        }
      }, [isBundleOpen, isBundleLoading, currentSelectedRows.length]);

      //ComponentWillUnmount (necessary approach because of dynamic state values
      const lastChangesRef = useRef({
        bundleId,
        view: modeActive,
        selectedOrders: currentSelectedRows,
        draftOrderList: [],
      });
      useEffect(() => {
        lastChangesRef.current = {
          bundleId,
          view: modeActive,
          selectedOrders: currentSelectedRows,
          draftOrderList: [],
        };
      }, [bundleId, currentSelectedRows, isBundleOpen, modeActive]);

      useEffect(() => {
        return () => {
          if (
            lastChangesRef.current.view !== 'normal' ||
            lastChangesRef.current.selectedOrders.length ||
            lastChangesRef.current.draftOrderList.length
          )
            dispatch({
              type: Types.StoreLastChangesInBundle,
              payload: {
                bundleId: lastChangesRef.current.bundleId,
                view: lastChangesRef.current.view,
                selectedOrders: lastChangesRef.current.selectedOrders,
                draftOrderList: lastChangesRef.current.draftOrderList,
              },
            });
        };
      }, [dispatch, isBundleOpen]);

      return (
        <div className="orders-table-wrapper">
          <div className="orders-table-container">
            <MonitorOrdersTable
              bundle={bundle}
              currentOrdersInstructions={currentOrdersInstructions}
              onSelectOrderInstruction={onSelectOrderInstruction}
              onSaveDndDraft={onSaveDndDraft}
            />
            {createFooter(
              modeActive,
              movePickupOnTop,
              onClickCancelBundleButton,
              triggerRecreateBundle,
              currentSelectedRows,
              backToBundleNormalView,
              toggleMoveOrdersModalCallback,
              reverseDeliveriesCallback,
              toggleShelfModalCallback,
              toggleETAModalCallback,
              reorderButtonActionCallback,
              toggleChangePickupPointModalCallback
            )}
          </div>
        </div>
      );
    },
    checkProps
  );

MonitorOrdersTableWrapper.displayName = 'MonitorOrdersTableWrapper';
export { MonitorOrdersTableWrapper };
